import React, { useContext, useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import enLocale from 'date-fns/locale/en-GB';
import { useQuery } from '@tanstack/react-query';
import { useCookies } from 'react-cookie';
import { AppsSection } from '@eposnow/ui-core';
import localesMap from '../i18n/localesMap.json';
import ErrorLoadingData from '../components/ErrorLoadingData';
import { API_V1_HOSTNAME_KEY } from '../localStorageKeys';
import { ApiContext, ErrorObject } from './ApiContext';
import useClaims from '../helpers/useClaims';
import { UIContext } from './UIContext';
import { AppsMenuResponse, User } from '../types';
import AuthenticatingSpinner from '../components/AuthenticatingSpinner';

const DEFAULT_LOCALE = 'en-GB';

const { locales } = localesMap;

const UserContext = React.createContext<{
  user?: User;
  locale: string;
  localeFns: Locale;
  isLoadingUserApps: boolean;
  cdnUrl: string;
  appsMenu: AppsSection[][];
  isErrorAppsMenu: boolean;
  refetchAppsMenu: () => void;
}>({
  user: undefined,
  locale: DEFAULT_LOCALE,
  localeFns: enLocale,
  isLoadingUserApps: false,
  cdnUrl: '',
  appsMenu: [],
  isErrorAppsMenu: false,
  refetchAppsMenu: () => {
    /* void */
  },
});

const UserContextProvider = ({ children }: { children: JSX.Element }) => {
  const { t, i18n } = useTranslation();
  const [locale, setLocale] = useState<string>(DEFAULT_LOCALE);
  const [localeFns, setLocaleFns] = useState<Locale>(enLocale);
  const { setColorModeFromBoolean, setAnimationsDisabledFromBoolean } = useContext(UIContext);
  const { fetchIsReady, apiFetch } = useContext(ApiContext);
  const [cookies, setCookie] = useCookies(['useDarkTheme', 'disableAnimation']);
  const [cdnUrl, setCdnUrl] = useState('');
  const [appsMenu, setAppsMenu] = useState<AppsSection[][]>([]);
  const { claims } = useClaims();

  const importLocaleFile = async (lang: string) => {
    const localeToSet = await import(`date-fns/locale/${lang}/index.js`);
    setLocaleFns(localeToSet.default);
  };

  const changeLanguage = (language: string) => {
    const lang = locales[language] || DEFAULT_LOCALE;
    const dateLang = locales[lang] || DEFAULT_LOCALE;

    setLocale(lang);
    i18n.changeLanguage(lang).then(() => {
      importLocaleFile(dateLang).then();
    });
    document.documentElement.setAttribute('lang', lang);
  };

  const {
    refetch: fetchData,
    data: user,
    isLoading,
    isError,
    error,
  } = useQuery<User, ErrorObject>({
    queryKey: ['user'],
    queryFn: () => apiFetch(`${localStorage.getItem(API_V1_HOSTNAME_KEY)}user`),
    enabled: fetchIsReady,
    staleTime: 0,
  });

  useEffect(() => {
    if (user?.locale) {
      changeLanguage(user.locale);
    }
  }, [user?.locale]);

  const { data: uiPreferences } = useQuery({
    queryKey: ['uiPreferences', claims?.sub],
    queryFn: () =>
      apiFetch(`${localStorage.getItem(API_V1_HOSTNAME_KEY)}users/${claims?.sub}/ui-preferences`),
    enabled: fetchIsReady && !!claims?.sub,
    staleTime: 0,
  });

  useEffect(() => {
    if (uiPreferences) {
      setCookie('useDarkTheme', uiPreferences.useDarkTheme);
      setCookie('disableAnimation', uiPreferences.disableAnimation);
    }
  }, [uiPreferences, setCookie]);

  useEffect(() => {
    setColorModeFromBoolean(uiPreferences?.useDarkTheme || cookies.useDarkTheme);
    setAnimationsDisabledFromBoolean(uiPreferences?.disableAnimation || cookies.disableAnimation);
  }, [
    uiPreferences,
    cookies.useDarkTheme,
    cookies.disableAnimation,
    setColorModeFromBoolean,
    setAnimationsDisabledFromBoolean,
  ]);

  const {
    data: appsMenuResponse,
    isLoading: isLoadingUserApps,
    isError: isErrorAppsMenu,
    refetch: refetchAppsMenu,
  } = useQuery<AppsMenuResponse, ErrorObject>({
    queryKey: ['userApps', user?.id],
    queryFn: () =>
      apiFetch(`${localStorage.getItem(API_V1_HOSTNAME_KEY)}apps`, {}, 'GET', false, {
        'Accept-Language': user?.locale,
      }),
    enabled: fetchIsReady && !!claims && !!claims?.sub && !!user,
    staleTime: 0,
  });

  useEffect(() => {
    if (appsMenuResponse) {
      setCdnUrl(appsMenuResponse.baseUrl);
      setAppsMenu(appsMenuResponse.items);
    }
  }, [appsMenuResponse]);

  if (window.location.pathname.includes('logged-out')) return null;

  if (isError) {
    if (error?.code === 401) {
      return <AuthenticatingSpinner />;
    }

    return <ErrorLoadingData action={fetchData} />;
  }

  if (isLoading || !user) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '96px',
        }}
      >
        <CircularProgress
          aria-label={t('components.loading.loadingUserData')}
          aria-live="polite"
          aria-busy={isLoading || !user}
          data-testid="loading-icon"
          sx={{ color: 'text.secondary' }}
        />
      </Box>
    );
  }

  return (
    <UserContext.Provider
      value={{
        user,
        locale,
        localeFns,
        isLoadingUserApps,
        cdnUrl,
        appsMenu,
        isErrorAppsMenu,
        refetchAppsMenu,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export { UserContext, UserContextProvider };

